import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  SpaceBetween,
  Button,
  Header,
  Checkbox,
  Flashbar,
  Spinner
} from "@amzn/awsui-components-react";
import { capitalizeFirstLetter, downloadJsonData } from "src/Ateam-BI/utils/generalUtils";
import { AppData, ChartAccountsObject, DownloadAppModalProps, getProcessResponse, GetTancDetailsResponse, OutputObject, PermissionObject, PreferenceItem, ProcessData, ProcessesData, SchemeTableObject, TancDetails } from "./Types/downloadAppModal";
import { PROCESS_TYPE_CONFIG, RIPPLE_DETAILS_TO_REMOVE, RIPPLE_PARAMETER_DETAILS_TO_REMOVE, TANC_INFO } from "./Constants/downloadAppModal";
import { TEMPLATE_APP } from "../Constants";
import { getAllRipplesSync, listPreference, tancAssociationStatusSync } from "src/utilities/AppSyncPlusAPIs";
import { RippleIDTypes } from "src/generic-components/Ripple/Constants";

const DownloadAppModal = ({ 
  processes,
  appData, 
  hideModal
}: DownloadAppModalProps) => {
  const [processesToInclude, setProcessesToInclude] = useState<Set<string>>(new Set());
  const [checkAll, setCheckAll] = useState(true);
  const [loadingFullProcessInfoMap, setLoadingFullProcessInfoMap] = useState(false)
  const [fullProcessInfoMap, setFullProcessInfoMap] = useState<{[key: string]: ProcessData}>({})

  const getProcessSetId = (processType:string, process:ProcessData) => {
    const {processKey} = PROCESS_TYPE_CONFIG[processType];
    return `${processType}#${process[processKey]}`;
  }

  const createProcessesSet = (allProcesses: ProcessesData) => {
    return Object.keys(allProcesses).reduce((processSet:Set<string>, processType:string) => {
      allProcesses[processType].forEach((process:ProcessData) => processSet.add(getProcessSetId(processType, process)))
      return processSet;
    }, new Set<string>())
  }

  const getTancPreference = async (tancId: string, preferenceType: string):Promise<PreferenceItem[]> => {
    return new Promise((resolve, reject) => {
      listPreference(tancId, TANC_INFO.TANC, preferenceType, function onSuccess(response:GetTancDetailsResponse) {
        const items = response?.data?.listPreference?.items;
        const preference = items.map((item):PreferenceItem => {
          const information = item?.information ? JSON.parse(item.information) : [];
          const extras = item?.extras ? JSON.parse(item.extras) : {};
          return {
            information,
            extras,
            label: item.label
          };
        })
        resolve(preference)
      }, function onFailure(response:any) {console.error(response); reject(response)})
    })
  }

  const getTancPermissions = async (tancId: string):Promise<PreferenceItem[]> => {
    return getTancPreference(tancId, TANC_INFO.PERMISSIONS);
  }

  const getTancFilters = async (tancId: string):Promise<PreferenceItem[]> => {
    return getTancPreference(tancId, TANC_INFO.FILTER);
  }

  const getTancConfiguration = async (tancId: string):Promise<PreferenceItem[]> => {
    return getTancPreference(tancId, TANC_INFO.CONFIGURATION);
  }

  const getTancDetails = async (process: ProcessData, processType: string):Promise<TancDetails | undefined> => {
    const {processKey, tancKey} = PROCESS_TYPE_CONFIG[processType];
    if (tancKey) {
      const tancId = await tancAssociationStatusSync(process[processKey], tancKey);
      if (tancId) {
        const [filters, permissions, configuration] = await Promise.all([getTancFilters(tancId), getTancPermissions(tancId), getTancConfiguration(tancId)])
        return {
          filters,
          permissions,
          configuration
        }
      }
    }
    return undefined;
  }

  const getRipples = async (processData:any) => {
    const mappingId = processData.mapping_id;
    if (mappingId) {
      const ripples = await getAllRipplesSync(mappingId, RippleIDTypes.FLEXICO);
      if (ripples?.data?.getAllRipples) {
        const cleanedRipples = ripples?.data?.getAllRipples.map((ripple:any) => {
          const cleanedRipple = structuredClone(ripple);
          if (!cleanedRipple.approvers) cleanedRipple.approvers = [];
          RIPPLE_DETAILS_TO_REMOVE.forEach(rippleDetail => {delete cleanedRipple[rippleDetail];})
          cleanedRipple.parameters = cleanedRipple.parameters.map((parameter: any) => {
            RIPPLE_PARAMETER_DETAILS_TO_REMOVE.forEach(rippleParameterDetail => {delete parameter[rippleParameterDetail];})
            return parameter;
          })
          return cleanedRipple;
        })
        return cleanedRipples;
      }
    }
  }

  const loadFullProcessInfoMap = async (allProceses: ProcessesData) => {
    setLoadingFullProcessInfoMap(true)
    const processPromiseList = Object.keys(allProceses).reduce((promiseList:Promise<any>[], processType) => {
      const {getProcess, processKey, getProcessResponse} = PROCESS_TYPE_CONFIG[processType];
      allProceses[processType].map(process => {
        const id = process[processKey]
        if (id && typeof id === "string") {
          promiseList.push(new Promise((resolve) => {
            getProcess.apply(this, [id, async (response:getProcessResponse) => {
              const r = response.data[getProcessResponse];
              const processSetId = getProcessSetId(processType, process);
              fullProcessInfoMap[processSetId] = r;
              const [tancDetails, rippleDetails] = await Promise.all(
                [getTancDetails(process, processType), getRipples(r)]
              )
              tancDetails && (fullProcessInfoMap[processSetId].tanc = tancDetails);
              rippleDetails && (fullProcessInfoMap[processSetId].ripples = rippleDetails)
              setFullProcessInfoMap(fullProcessInfoMap)
              resolve(r)
        }])}))
      } else {
          promiseList.push(new Promise((resolve) => resolve({})))
        }
      })
      return promiseList;
    }, [])
    await Promise.all(processPromiseList)
    setLoadingFullProcessInfoMap(false)
  }

  useEffect(() => {
    loadFullProcessInfoMap(processes);
  }, [processes])

  useEffect(() => {
    checkAll ? 
      setProcessesToInclude(new Set(createProcessesSet(processes)))
      : setProcessesToInclude(new Set());
  }, [checkAll, processes])

  const getProcessesToDownload = (allProcesses:ProcessesData) => {
    let processesToDownload: {[key:string]:ProcessData[]} = {};
    Object.keys(allProcesses).forEach((processType:string) => {

      const processToDownloadOfType = allProcesses[processType].filter(process => {
        const processSetId = getProcessSetId(processType, process);
        return processesToInclude.has(processSetId);
      })
      
      processesToDownload[processType] = processToDownloadOfType.map((process) => {
        const {processKey, removeKeys} = PROCESS_TYPE_CONFIG[processType];
        const processSetId = getProcessSetId(processType, process);
        const fullProcessInfo = structuredClone(fullProcessInfoMap[processSetId]);
        delete fullProcessInfo[processKey];
        removeKeys.forEach(removeKey => delete fullProcessInfo[removeKey])
        return fullProcessInfo;
      })

    })
    return processesToDownload;
  }

  const filterDataKeys = (appData: AppData) => {
    const filteredDataObject = structuredClone(appData);
    delete filteredDataObject[TEMPLATE_APP.APP_ID]
    filteredDataObject.permission = filteredDataObject?.permission?.map((perm:PermissionObject) => {
      delete perm["app_permission_pk"];
      return perm;
    })
    filteredDataObject.output = filteredDataObject?.output?.map((perm:OutputObject) => {
      delete perm["app_output_pk"];
      return perm;
    })
    filteredDataObject.source_data_tables = filteredDataObject?.source_data_tables?.map((perm:SchemeTableObject) => {
      delete perm["source_table_pk"];
      return perm;
    })
    filteredDataObject.chart_of_accounts = filteredDataObject?.chart_of_accounts?.map((perm:ChartAccountsObject) => {
      delete perm["chart_of_accounts_pk"];
      return perm;
    })

    return filteredDataObject;
  }

  const downloadData = (appData: AppData, allProcesses:ProcessesData) => {
    const filename = appData.app_name;
    const downloadObject = filterDataKeys(appData);
    downloadObject.processes = getProcessesToDownload(structuredClone(allProcesses));
    downloadJsonData(filename, downloadObject);
  }

  return (
    <Modal
      onDismiss={hideModal}
      visible={true}
      header={`Download App`}
      footer={
        <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={hideModal}>Cancel</Button>
                <Button variant="primary" disabled={loadingFullProcessInfoMap} onClick={() => {
                    downloadData(appData, processes)
                }}>
                  {loadingFullProcessInfoMap && (<Spinner/>)} Download
                </Button>
            </SpaceBetween>
        </Box>
      }
      size="medium"
    >
      <Box margin={{bottom:"m"}}>
        <Box margin={{bottom:"m"}}>
          <Flashbar
            items={[{
                type: "info",
                content: "Links for QuickSights Dashboards will need to be updated upon upload to prod",
                dismissible: false,
            }]} 
          />
        </Box>
        <Header variant="h2">Processes to Include:</Header>
        <Box margin={{left:"m", top:"xs", bottom:"s"}}>
          <Checkbox
            checked={checkAll}
            onChange={()=>{setCheckAll(!checkAll)}}
          > Select/Deselect All </Checkbox>
        </Box>

        <Box margin={{left:"m"}}>
          <SpaceBetween size="m">
            {Object.keys(processes).map((processType:string, index: number)  => {
              return (
                <Box key={index}>
                  <Header variant="h3">{capitalizeFirstLetter(processType)}</Header>
                  <Box margin={{left:"m"}}>
                    {processes[processType].length > 0 ? processes[processType].map((process:ProcessData, innerIndex: number) => {
                      const {processLabel} = PROCESS_TYPE_CONFIG[processType];
                      const processSetId = getProcessSetId(processType, process);
                      return (
                        <Checkbox 
                          key={innerIndex}
                          checked={processesToInclude.has(processSetId)}
                          onChange={() => {
                            processesToInclude.has(processSetId)
                              ? processesToInclude.delete(processSetId)
                              : processesToInclude.add(processSetId);
                            setProcessesToInclude(new Set(processesToInclude))
                          }}
                        >{(typeof process[processLabel] === "string") && process[processLabel]}</Checkbox>
                      )
                    }) : (
                      <Box color="text-status-inactive">None Available</Box>
                    )}
                  </Box>
                </Box>
              )
            })}
          </SpaceBetween>
        </Box>
      </Box>
    </Modal>
  );
};

export default DownloadAppModal;
